import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import InquiryForm from './InquiryForm';
import { Toaster } from './ui/toaster';

const AnimatedCounter = ({ target, suffix = '', prefix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const startTime = Date.now();
          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * target));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration, hasAnimated]);

  return (
    <span ref={ref}>
      {prefix}{count}{suffix}
    </span>
  );
};

const Hero = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  const skylineImages = [
    'https://customer-assets.emergentagent.com/job_130b504f-d356-4ffc-9f88-b9ceecab1da1/artifacts/7qwaf5ge_Houston.jpeg',
    'https://customer-assets.emergentagent.com/job_130b504f-d356-4ffc-9f88-b9ceecab1da1/artifacts/qva38gux_Austin.jpeg',
    'https://customer-assets.emergentagent.com/job_130b504f-d356-4ffc-9f88-b9ceecab1da1/artifacts/rr02gfd1_Chicago.jpeg',
    'https://customer-assets.emergentagent.com/job_130b504f-d356-4ffc-9f88-b9ceecab1da1/artifacts/4x00m7ja_Dallas.jpeg',
    'https://customer-assets.emergentagent.com/job_130b504f-d356-4ffc-9f88-b9ceecab1da1/artifacts/jh3u3bcf_Los%20Angeles.jpeg',
    'https://customer-assets.emergentagent.com/job_130b504f-d356-4ffc-9f88-b9ceecab1da1/artifacts/0vmcoyj2_New%20York.jpeg',
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % skylineImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [skylineImages.length]);

  const stats = [
    { label: 'Years of Industry Experience', value: 15, suffix: '+', prefix: '' },
    { label: 'Number of\nProjects Funded', value: 100, suffix: 's', prefix: '' },
    { label: 'Minimum Loan Amount', value: 150, suffix: 'K', prefix: '$' },
    { label: 'Maximum\nLoan Amount', value: 50, suffix: 'M', prefix: '$' },
  ];

  return (
    <>
      <section data-testid="hero-section" className="relative min-h-[90vh] bg-slate-900 pt-20 overflow-hidden" aria-label="Close Now Lending - Nationwide real estate investment loans, hard money, fix and flip, DSCR, commercial lending">
        {/* Full-width rotating background images */}
        {skylineImages.map((img, index) => (
          <div
            key={index}
            role="img"
            aria-label={`Close Now Lending serves real estate investors nationwide - City skyline ${index + 1}`}
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out"
            style={{
              backgroundImage: `url(${img})`,
              opacity: index === currentImage ? 1 : 0,
            }}
          />
        ))}
        {/* White wash overlay to soften images and keep text readable */}
        <div className="absolute inset-0 bg-white/40"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/50"></div>

        <div className="container mx-auto px-4 h-full relative z-10">
          <div className="flex flex-col justify-center min-h-[calc(90vh-80px)] py-12 lg:py-20 max-w-3xl">
            {/* Content */}
            <div className="animate-fade-in-up">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight drop-shadow-lg">
                One-Stop Solution{' '}
                <span className="text-blue-400">for Real Estate</span>{' '}
                Financing
              </h1>

              <p className="text-lg text-gray-200 mb-10 max-w-xl leading-relaxed drop-shadow">
                <span className="font-bold text-white">Close Now Lending</span> is a full service, nationwide mortgage originator that specializes in financing real estate investors. We <em className="font-bold">are</em> the subject matter experts:
              </p>

              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
                {stats.map((stat, index) => (
                  <div key={index} data-testid={`stat-${index}`} className="text-center bg-slate-900/40 backdrop-blur-sm rounded-xl py-3 px-2">
                    <div className="text-3xl md:text-4xl font-bold text-white mb-1 drop-shadow">
                      <AnimatedCounter
                        target={stat.value}
                        suffix={stat.suffix}
                        prefix={stat.prefix}
                        duration={1500}
                      />
                    </div>
                    <div className="text-xs md:text-sm text-gray-300 whitespace-pre-line leading-tight">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <Button
                  data-testid="hero-get-started-btn"
                  onClick={() => setIsFormOpen(true)}
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg font-semibold rounded-md transition-all duration-300 hover:shadow-lg hover:shadow-blue-600/30"
                >
                  Get Started
                </Button>
                <Button
                  data-testid="hero-apply-btn"
                  onClick={() => setIsFormOpen(true)}
                  size="lg"
                  variant="outline"
                  className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold rounded-md transition-all duration-300"
                >
                  Apply Now
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Dots indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {skylineImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImage(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentImage ? 'bg-white w-6' : 'bg-white/40'
              }`}
            />
          ))}
        </div>
      </section>

      <InquiryForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        loanType=""
      />
      <Toaster />
    </>
  );
};

export default Hero;
